// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { faro } from '@grafana/faro-web-sdk';
import { NextPage } from 'next';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Ad from '../../../components/Ad';
import ErrorState from '../../../components/ErrorState';
import Footer from '../../../components/Footer';
import Layout from '../../../components/Layout';
import ProductPrice from '../../../components/ProductPrice';
import Recommendations from '../../../components/Recommendations';
import Select from '../../../components/Select';
import { CypressFields } from '../../../utils/Cypress';
import ApiGateway from '../../../gateways/Api.gateway';
import { recommendationsQueryOptions } from '../../../gateways/recommendationsQuery';
import { Product } from '../../../protos/demo';
import AdProvider from '../../../providers/Ad.provider';
import { useCart } from '../../../providers/Cart.provider';
import * as S from '../../../styles/ProductDetail.styled';
import { useCurrency } from '../../../providers/Currency.provider';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { getImageWithDelay } from '../../../utils/DelayedImage';
import SessionGateway from '../../../gateways/Session.gateway';
import { getCompareAtPrice, getProductMeta } from '../../../utils/ProductMeta';

const quantityOptions = new Array(10).fill(0).map((_, i) => i + 1);

const ProductDetail: NextPage = () => {
  const { geography, device } = SessionGateway.getSession();
  const { push, query } = useRouter();
  const [quantity, setQuantity] = useState(1);
  const {
    addItem,
    cart: { items },
  } = useCart();
  const { selectedCurrency } = useCurrency();
  const productId = query.productId?.toString() ?? "Unknown";
  const queryClient = useQueryClient();
  const productIds = useMemo(
    () => [productId, ...items.map(({ productId }) => productId)],
    [productId, items]
  );

  const slowLoadEnabled = useBooleanFlagValue('imageSlowLoad', false);
  const [imageSrc, setImageSrc] = useState<string>('');

  useEffect(() => {
    faro.api.startUserAction('open-product-detail', {
      type: 'interaction',
      productId,
      device: device,
      geography: geography
    });
  }, [productId]);

  // Dispatch the recommendations fetch right after the user action above so the
  // request lands inside the 'open-product-detail' action window and Faro
  // attributes it to that action. AdProvider reads the result from the cache
  // (deferRecommendationsFetch). Re-runs on input change to keep recs fresh.
  useEffect(() => {
    queryClient.prefetchQuery(recommendationsQueryOptions(productIds, selectedCurrency));
  }, [queryClient, productIds, selectedCurrency]);

  const {
    data = {} as Product,
    isError: isProductError,
    refetch: refetchProduct,
  } = useQuery({
    queryKey: ['product', productId, 'selectedCurrency', selectedCurrency],
    queryFn: () => ApiGateway.getProduct(productId, selectedCurrency),
    enabled: !!productId,
  });

  const { name, picture, description, priceUsd = { units: 0, currencyCode: 'USD', nanos: 0 }, categories } = data;
  const meta = getProductMeta(productId);

  // react-query keeps the cached product when a refetch fails, so only take
  // over the page when there is nothing to render. Otherwise a failed
  // background refetch would pull a loaded product out from under the user.
  const showProductError = isProductError && !data.id;

  const onAddItem = useCallback(async () => {
    faro.api.startUserAction('add-product-to-cart', {
      type: 'interaction',
      productId,
      device: device,
      geography: geography
    });

    await addItem({
      productId,
      quantity,
    });
    push(`/cart?device=${device}&geography=${geography}`);
  }, [addItem, productId, quantity, push]);

  useEffect(() => {
    let objectUrl = '';
    let cancelled = false;
    if(picture){
      getImageWithDelay(picture, slowLoadEnabled).then(blob => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      });
    }
    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [picture, slowLoadEnabled]);

  return (
    <AdProvider
      productIds={productIds}
      contextKeys={[...new Set(categories)]}
      deferRecommendationsFetch
    >
      <Layout>
        <S.ProductDetail data-cy={CypressFields.ProductDetail}>
          {showProductError ? (
            <ErrorState
              title="We could not load this product"
              message="Something went wrong on our end. Try again in a moment."
              onRetry={refetchProduct}
            />
          ) : (
            <S.Container>
              <S.ImageWell>
                <S.Image $src={imageSrc} data-cy={CypressFields.ProductPicture} />
              </S.ImageWell>
              <S.Details>
                <S.MetaRow>
                  {!!categories?.length && <S.Category>{categories[0]}</S.Category>}
                  {meta.badge && <S.BadgePill $variant={meta.badge}>{meta.badge}</S.BadgePill>}
                </S.MetaRow>
                <S.Name data-cy={CypressFields.ProductName}>{name}</S.Name>
                <S.Rating aria-label={`Rated ${meta.rating.toFixed(1)} out of 5 from ${meta.reviewCount} reviews`}>
                  <S.Stars>
                    {Array.from({ length: 5 }, (_, index) => (
                      <S.Star key={index} $filled={index < Math.round(meta.rating)}>
                        ★
                      </S.Star>
                    ))}
                  </S.Stars>
                  <span>
                    {meta.rating.toFixed(1)} · {meta.reviewCount} reviews
                  </span>
                </S.Rating>
                <S.ProductPrice>
                  <ProductPrice price={priceUsd} />
                  {meta.badge === 'Sale' && (
                    <s>
                      <ProductPrice price={getCompareAtPrice(priceUsd)} />
                    </s>
                  )}
                </S.ProductPrice>
                <S.Stock>In stock, ships within 24 hours</S.Stock>
                <S.Text>Quantity</S.Text>
                <S.PurchaseRow>
                  <Select
                    data-cy={CypressFields.ProductQuantity}
                    onChange={event => setQuantity(+event.target.value)}
                    value={quantity}
                  >
                    {quantityOptions.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                  <S.AddToCart data-cy={CypressFields.ProductAddToCart} onClick={onAddItem}>
                    Add to cart
                  </S.AddToCart>
                </S.PurchaseRow>
                <S.Description data-cy={CypressFields.ProductDescription}>{description}</S.Description>
                <S.TrustList>
                  <li>
                    <img src="/icons/Check.svg" alt="" /> Free shipping over $50
                  </li>
                  <li>
                    <img src="/icons/Check.svg" alt="" /> 30-day returns
                  </li>
                  <li>
                    <img src="/icons/Check.svg" alt="" /> Tracked from cart to doorstep
                  </li>
                </S.TrustList>
              </S.Details>
            </S.Container>
          )}
          <Recommendations />
        </S.ProductDetail>
        <Ad />
        <Footer />
      </Layout>
    </AdProvider>
  );
};

export default ProductDetail;
